import { z } from "zod";
import {
  analyticsApiKeyItemSchema,
  analyticsEndpointItemSchema,
  analyticsOverviewDataSchema,
  analyticsRecentRequestItemSchema,
  analyticsTimeSeriesItemSchema,
} from "./analyticsDataTypes.ts";

/** Key, request-type and status filters apply across the report; requestId and page affect history only. Key IDs are public identifiers, never raw keys or hashes. */
export const analyticsReportFiltersSchema = z.object({
  apiKeyId: z.string().min(1).max(256).optional(),
  requestType: z.string().min(1).max(256).optional(),
  status: z.enum(["all", "success", "4xx", "5xx"]).default("all"),
  granularity: z.enum(["minute", "hour", "day", "week"]).default("day"),
  requestId: z.string().trim().max(128).default(""),
  page: z.coerce.number().int().min(1).max(400).default(1),
});

/** Maximum encoded cursor length in characters, shared by requests, responses and the server codec. */
export const ANALYTICS_HISTORY_CURSOR_MAX_LENGTH = 4096;

/** History filters have no aggregate granularity or page offset; each cursor advances within the authorized range. */
export const analyticsHistoryFiltersSchema = analyticsReportFiltersSchema
  .pick({ apiKeyId: true, requestType: true, status: true, requestId: true })
  .extend({
    cursor: z.string().max(ANALYTICS_HISTORY_CURSOR_MAX_LENGTH).optional(),
  });

/** One independently read history page. No total is inferred from a bounded scan; a continuation can exist even when no rows matched. */
export const analyticsHistorySchema = z.object({
  requests: z.array(analyticsRecentRequestItemSchema).max(25),
  nextCursor: z.string().max(ANALYTICS_HISTORY_CURSOR_MAX_LENGTH).nullable(),
  scanned: z.number().int().min(0).max(500),
  scanLimited: z.boolean(),
});

/** Validated public filters for cursor history and exact-ID lookup. */
export type AnalyticsHistoryFilters = z.infer<
  typeof analyticsHistoryFiltersSchema
>;
/** Operational rows and continuation state, independent of aggregate sampling. */
export type AnalyticsHistory = z.infer<typeof analyticsHistorySchema>;

/** Settled prepaid usage in exact minor-unit atoms; unavailable totals are never represented as zero. */
export const analyticsSpendSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("ready"),
    currency: z.literal("usd"),
    totalAtoms: z.string().regex(/^(0|-?[1-9][0-9]*)$/),
    timeseries: z.array(
      z.object({
        timestamp: z.string(),
        amountAtoms: z.string().regex(/^(0|-?[1-9][0-9]*)$/),
      }),
    ),
  }),
  z.object({
    status: z.literal("unavailable"),
    reason: z.enum([
      "admin_required",
      "request_filters",
      "not_supported",
      "read_failed",
      "range_too_large",
    ]),
  }),
]);
/** Spend graph contract; arithmetic remains exact until chart-coordinate conversion. */
export type AnalyticsSpend = z.infer<typeof analyticsSpendSchema>;

/** A bounded, consistently filtered report; endpoint percentiles are null without samples. */
export const analyticsReportSchema = z.object({
  spend: analyticsSpendSchema.optional(),
  overview: analyticsOverviewDataSchema.omit({ previousPeriod: true }),
  timeseries: z.array(analyticsTimeSeriesItemSchema),
  endpoints: z.array(
    analyticsEndpointItemSchema.extend({
      latencySamples: z.number().optional(),
      timeseries: z
        .array(
          z.object({
            timestamp: z.string(),
            requests: z.number(),
            errors: z.number(),
            audioSeconds: z.number(),
            inputTokens: z.number().nullable().optional(),
            outputTokens: z.number().nullable().optional(),
            inputTokenRequests: z.number().optional(),
            outputTokenRequests: z.number().optional(),
            p50LatencyMs: z.number().nullable(),
            p95LatencyMs: z.number().nullable(),
          }),
        )
        .optional(),
    }),
  ),
  apiKeys: z.array(
    analyticsApiKeyItemSchema.extend({ latencySamples: z.number().optional() }),
  ),
  requests: z.array(analyticsRecentRequestItemSchema),
  options: z.object({
    apiKeys: z.array(z.object({ id: z.string(), name: z.string() })),
    requestTypes: z.array(z.string()),
  }),
  pagination: z.object({
    page: z.number(),
    pageSize: z.number(),
    total: z.number(),
  }),
  truncated: z.boolean(),
});

/** Parsed report filters accepted by the authenticated analytics reader. */
export type AnalyticsReportFilters = z.infer<
  typeof analyticsReportFiltersSchema
>;
/** Report DTO contains operational metadata only, with no clinical bodies or credentials. */
export type AnalyticsReport = z.infer<typeof analyticsReportSchema>;
